import { useEffect, useMemo, useState } from 'react';
import { getColumnHeader, SearchType } from './config/searchConfig';
import { getResultColumnValue } from './helpers/resultValueHelpers';
import { deduplicateSuggestionsData } from './helpers/deduplicateSuggestions';
import { formatSimilarityScore } from './helpers/formatSimilarityScore';
import {
  getSecondaryColumnValue,
  getTertiaryColumnValue,
  hasTertiaryColumn,
  INITIAL_SUGGESTIONS_SORT,
  nextSuggestionsSortState,
  SortableColumnKey,
  sortSuggestionRows,
  SuggestionsSortState,
} from './helpers/sortSuggestionsTable';
import { ArrowsUpDownIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

interface SuggestionsTableProps {
  suggestionsData: any[];
  selectedItems: any[];
  handleCheckboxSelection: (result: any) => void;
  searchType: string;
  selectedFilter: string;
}

type TableColumn =
  | { key: 'rowNum' | 'select'; label: string; sortable: false }
  | { key: SortableColumnKey; label: string; sortable: true };

const SuggestionsTable = ({
  suggestionsData,
  selectedItems,
  handleCheckboxSelection,
  searchType,
  selectedFilter,
}: SuggestionsTableProps) => {
  const [sortState, setSortState] = useState<SuggestionsSortState>(INITIAL_SUGGESTIONS_SORT);

  useEffect(() => {
    setSortState(INITIAL_SUGGESTIONS_SORT);
  }, [suggestionsData, searchType, selectedFilter]);

  const deduplicatedData = useMemo(() => deduplicateSuggestionsData(suggestionsData), [suggestionsData]);

  const selectedIds = useMemo(() => {
    return new Set(selectedItems.map(item => JSON.stringify(item)));
  }, [selectedItems]);

  const showSimilarityColumn =
    selectedFilter === 'name' &&
    (searchType === 'DISEASES' || searchType === 'GENES' || searchType === 'CHEMICALS');

  const showTertiary = hasTertiaryColumn(searchType, selectedFilter);

  const columns = useMemo<TableColumn[]>(() => {
    const cols: TableColumn[] = [
      { key: 'rowNum', label: '#', sortable: false },
      {
        key: 'primary',
        label: getColumnHeader(searchType as SearchType, selectedFilter, 'primary'),
        sortable: true,
      },
      {
        key: 'secondary',
        label: getColumnHeader(searchType as SearchType, selectedFilter, 'secondary'),
        sortable: true,
      },
    ];

    if (showTertiary) {
      cols.push({
        key: 'tertiary',
        label: getColumnHeader(searchType as SearchType, selectedFilter, 'tertiary'),
        sortable: true,
      });
    }

    if (showSimilarityColumn) {
      cols.push({ key: 'similarity', label: 'Similarity Score', sortable: true });
    }

    cols.push({ key: 'select', label: 'Select', sortable: false });
    return cols;
  }, [searchType, selectedFilter, showSimilarityColumn, showTertiary]);

  const sortedData = useMemo(
    () => sortSuggestionRows(deduplicatedData, sortState, searchType, selectedFilter),
    [deduplicatedData, sortState, searchType, selectedFilter],
  );

  const colSpanForErrorRow = Math.max(columns.length - 2, 1);

  if (!Array.isArray(suggestionsData) || suggestionsData.length === 0) return null;

  const handleSortClick = (column: SortableColumnKey) => {
    setSortState(current => nextSuggestionsSortState(current, column));
  };

  const renderSortIndicator = (column: SortableColumnKey) => {
    if (sortState.column !== column) {
      return <ArrowsUpDownIcon className="h-3.5 w-3.5 suggestions-table-sort-idle" aria-hidden="true" />;
    }
    return sortState.direction === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5 suggestions-table-sort-active" aria-hidden="true" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5 suggestions-table-sort-active" aria-hidden="true" />
    );
  };

  const renderErrorRow = (item: any, rowNum: number) => (
    <tr key={`error-${item.query}`} className="validation-error">
      <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNum}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }} colSpan={colSpanForErrorRow}>
        {item.validationError}
      </td>
    </tr>
  );

  const renderNotFoundRow = (item: any, rowNum: number) => (
    <tr key={`notfound-${item.query}`} className="validation-error">
      <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNum}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }} colSpan={colSpanForErrorRow}>
        Not available
      </td>
    </tr>
  );

  const renderResultRow = (result: any, query: string, rowNum: number) => {
    const resultString = JSON.stringify(result);
    const isSelected = selectedIds.has(resultString);
    const primaryValue = getResultColumnValue(searchType, selectedFilter, result, query);
    const secondaryValue = getSecondaryColumnValue(searchType, selectedFilter, result);

    const cells = [<td key="row-num">{rowNum}</td>, <td key="primary">{primaryValue}</td>];

    cells.push(<td key="secondary">{secondaryValue}</td>);

    if (showTertiary) {
      cells.push(<td key="tertiary">{getTertiaryColumnValue(searchType, selectedFilter, result)}</td>);
    }

    if (showSimilarityColumn) {
      cells.push(<td key="similarity">{formatSimilarityScore(result.similarity)}</td>);
    }

    cells.push(
      <td key="checkbox">
        <input
          type="checkbox"
          name="selectedItem"
          checked={isSelected}
          onChange={() => handleCheckboxSelection(result)}
          className="ml-3"
          data-demo-tour="advanced-search-row-checkbox"
        />
      </td>,
    );

    return <tr key={`result-${rowNum}-${resultString}`}>{cells}</tr>;
  };

  return (
    <div className="suggestions-table" data-demo-tour="advanced-search-matches-table">
      <div className="suggestions-table-scroll-x">
        <table className="table panther-table">
          <thead>
            <tr className="suggestions-table-header">
              {columns.map(column => {
                if (!column.sortable) {
                  return <th key={column.key}>{column.label}</th>;
                }

                const isActive = sortState.column === column.key;
                const ariaSort = !isActive
                  ? 'none'
                  : sortState.direction === 'asc'
                    ? 'ascending'
                    : 'descending';

                return (
                  <th key={column.key} aria-sort={ariaSort}>
                    <button
                      type="button"
                      className={`suggestions-table-sort-btn${isActive ? ' is-active' : ''}`}
                      onClick={() => handleSortClick(column.key)}
                      title={`Sort by ${column.label}`}
                      aria-label={`Sort by ${column.label}`}
                    >
                      <span>{column.label}</span>
                      {renderSortIndicator(column.key)}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map(item => {
              if (item.type === 'error') {
                return renderErrorRow(item.data, item.rowNum);
              }
              if (item.type === 'notfound') {
                return renderNotFoundRow(item.data, item.rowNum);
              }
              return renderResultRow(item.data, item.query, item.rowNum);
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuggestionsTable;
